import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import {
  X,
  Copy,
  ExternalLink,
  Monitor,
  Smartphone,
  Globe,
  CloudUpload,
  CheckCircle2,
  XCircle,
  Loader2,
  Rocket,
} from 'lucide-react';
import { API_BASE_URL } from '../utils/api';

function fmtSize(bytes) {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function CopyButton({ text, tooltip = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const onClick = useCallback((e) => {
    e.stopPropagation();
    if (!text || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [text]);
  return (
    <Tooltip title={copied ? 'Copied!' : tooltip}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={!text}
          sx={{ color: copied ? '#34d399' : 'text.disabled' }}
        >
          {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function LinkRow({ label, url, icon: Icon }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderRadius: '6px',
        bgcolor: 'rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {Icon && <Icon size={14} strokeWidth={2} color="#a78bfa" />}
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', minWidth: 60 }}>{label}</Typography>
      <Typography
        sx={{
          flex: 1,
          fontSize: '0.72rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          color: url ? 'text.primary' : 'text.disabled',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {url || '—'}
      </Typography>
      <CopyButton text={url} />
      {url && (
        <Tooltip title="Open">
          <IconButton size="small" component="a" href={url} target="_blank" rel="noreferrer" sx={{ color: 'text.disabled' }}>
            <ExternalLink size={13} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function PlatformBlock({ icon: Icon, label, artifact, showMega }) {
  if (!artifact) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Icon size={14} strokeWidth={2} color="#a78bfa" />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', ml: 'auto' }}>
          {fmtSize(artifact.sizeBytes)}
          {typeof artifact.fileCount === 'number' && artifact.fileCount > 0 ? ` · ${artifact.fileCount} files` : ''}
        </Typography>
      </Box>
      <LinkRow label="R2" url={artifact.publicUrl || ''} />
      {showMega && <LinkRow label="MEGA" url={artifact.megaUrl || ''} />}
    </Box>
  );
}

function BuildStatusBanner({ build }) {
  if (!build) return null;
  if (build.status === 'succeeded') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Chip
          icon={<CheckCircle2 size={13} />}
          label="Build ready"
          size="small"
          sx={{ height: 22, bgcolor: 'rgba(52,211,153,0.15)', color: '#34d399', '& .MuiChip-icon': { color: '#34d399' } }}
        />
        {build.publishedAt && (
          <Chip
            icon={<Rocket size={12} />}
            label="Published"
            size="small"
            sx={{ height: 22, bgcolor: 'rgba(139,92,246,0.15)', color: '#a78bfa', '& .MuiChip-icon': { color: '#a78bfa' } }}
          />
        )}
      </Box>
    );
  }
  if (build.status === 'failed') {
    return (
      <Chip
        icon={<XCircle size={13} />}
        label={`Failed in ${build.error?.phase || '?'}`}
        size="small"
        sx={{ height: 22, bgcolor: 'rgba(248,113,113,0.15)', color: '#f87171', '& .MuiChip-icon': { color: '#f87171' } }}
      />
    );
  }
  return (
    <Chip
      icon={<Loader2 size={13} className="animate-spin" />}
      label={build.status}
      size="small"
      sx={{ height: 22, bgcolor: 'rgba(139,92,246,0.15)', color: '#a78bfa', '& .MuiChip-icon': { color: '#a78bfa' } }}
    />
  );
}

export default function BuildDetailsDialog({ buildId, onClose, onUpdated, getToken }) {
  const [build, setBuild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [megaSending, setMegaSending] = useState(false);
  const [megaCancelling, setMegaCancelling] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  const fetchBuild = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/builds/${encodeURIComponent(buildId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setBuild(data);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load build');
    } finally {
      setLoading(false);
    }
  }, [buildId, getToken]);

  useEffect(() => {
    fetchBuild();
  }, [fetchBuild]);

  // Poll while active.
  useEffect(() => {
    if (!build) return undefined;
    const active = ['dispatched', 'resolving', 'running', 'downloading', 'uploading'].includes(build.status)
      || (build.megaRequested && !build.megaUploadedAt && !build.megaError);
    if (!active) return undefined;
    const id = setInterval(fetchBuild, 5000);
    return () => clearInterval(id);
  }, [build, fetchBuild]);

  const handleMegaClick = useCallback(async () => {
    setMegaSending(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/builds/${encodeURIComponent(buildId)}/mega`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      await fetchBuild();
      onUpdated && onUpdated();
    } catch (e) {
      setError(e.message || 'Failed to trigger MEGA upload');
    } finally {
      setMegaSending(false);
    }
  }, [buildId, getToken, fetchBuild, onUpdated]);

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/builds/${encodeURIComponent(buildId)}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      await fetchBuild();
      onUpdated && onUpdated();
    } catch (e) {
      setError(e.message || 'Failed to publish build');
    } finally {
      setPublishing(false);
    }
  }, [buildId, getToken, fetchBuild, onUpdated]);

  const handleUnpublish = useCallback(async () => {
    setUnpublishing(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/builds/${encodeURIComponent(buildId)}/publish`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      await fetchBuild();
      onUpdated && onUpdated();
    } catch (e) {
      setError(e.message || 'Failed to unpublish build');
    } finally {
      setUnpublishing(false);
    }
  }, [buildId, getToken, fetchBuild, onUpdated]);

  const handleMegaCancel = useCallback(async () => {
    setMegaCancelling(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/builds/${encodeURIComponent(buildId)}/mega`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      await fetchBuild();
      onUpdated && onUpdated();
    } catch (e) {
      setError(e.message || 'Failed to cancel MEGA upload');
    } finally {
      setMegaCancelling(false);
    }
  }, [buildId, getToken, fetchBuild, onUpdated]);

  const title = build ? `${build.gameTitle || build.slug || 'Game'} V${build.version}` : '';
  const hasMegaLinks = build && (
    build.artifacts?.windows?.megaUrl
    || build.artifacts?.android?.megaUrl
    || build.artifacts?.pwa?.megaUrl
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {title || '—'}
          </Typography>
          {title && <CopyButton text={title} tooltip="Copy title" />}
          <Box sx={{ ml: 'auto' }}>
            <BuildStatusBanner build={build} />
          </Box>
        </Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
      >
        <X size={18} />
      </IconButton>
      <DialogContent sx={{ pt: 1 }}>
        {loading && !build ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>
        ) : build ? (
          <>
            {build.error?.message && (
              <Alert severity="error" sx={{ fontSize: '0.78rem', mb: 1.5 }}>
                {build.error.phase}: {build.error.message}
              </Alert>
            )}
            {build.workflowRunUrl && (
              <Box sx={{ mb: 1.5 }}>
                <Button
                  href={build.workflowRunUrl}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  startIcon={<ExternalLink size={12} />}
                  sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                >
                  View GitHub run
                </Button>
              </Box>
            )}

            <PlatformBlock
              icon={Monitor}
              label="Windows"
              artifact={build.platforms.includes('windows') ? build.artifacts?.windows : null}
              showMega={hasMegaLinks || build.megaRequested}
            />
            <PlatformBlock
              icon={Smartphone}
              label="Android"
              artifact={build.platforms.includes('android') ? build.artifacts?.android : null}
              showMega={hasMegaLinks || build.megaRequested}
            />
            <PlatformBlock
              icon={Globe}
              label="PWA"
              artifact={build.platforms.includes('pwa') ? build.artifacts?.pwa : null}
              showMega={false}
            />

            {build.status === 'succeeded' && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={build.publishedAt ? 'outlined' : 'contained'}
                  size="small"
                  startIcon={publishing ? <CircularProgress size={12} color="inherit" /> : <Rocket size={13} />}
                  onClick={handlePublish}
                  disabled={publishing || unpublishing}
                  sx={{
                    textTransform: 'none',
                    ...(build.publishedAt
                      ? { borderColor: '#34d399', color: '#34d399' }
                      : { bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }),
                  }}
                >
                  {build.publishedAt ? 'Re-publish' : 'Publish'}
                </Button>
                {build.publishedAt && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={unpublishing ? <CircularProgress size={12} color="inherit" /> : <XCircle size={13} />}
                    onClick={handleUnpublish}
                    disabled={publishing || unpublishing}
                    sx={{ textTransform: 'none' }}
                  >
                    {unpublishing ? 'Unpublishing…' : 'Unpublish'}
                  </Button>
                )}
                {build.manifestUrl && (
                  <Tooltip title="Open manifest JSON">
                    <IconButton
                      size="small"
                      component="a"
                      href={build.manifestUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: '#34d399' }}
                    >
                      <ExternalLink size={14} />
                    </IconButton>
                  </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={megaSending ? <CircularProgress size={12} color="inherit" /> : <CloudUpload size={13} />}
                  onClick={handleMegaClick}
                  disabled={megaSending || megaCancelling || (build.megaRequested && !build.megaError && !build.megaUploadedAt)}
                  sx={{ textTransform: 'none' }}
                >
                  {build.megaUploadedAt
                    ? 'Re-upload to MEGA'
                    : build.megaRequested && !build.megaError
                    ? 'MEGA upload in progress…'
                    : 'Upload to MEGA'}
                </Button>
                {build.megaRequested && !build.megaError && !build.megaUploadedAt && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={megaCancelling ? <CircularProgress size={12} color="inherit" /> : <XCircle size={13} />}
                    onClick={handleMegaCancel}
                    disabled={megaCancelling}
                    sx={{ textTransform: 'none' }}
                  >
                    {megaCancelling ? 'Cancelling…' : 'Cancel'}
                  </Button>
                )}
                {build.megaError && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#f87171' }}>
                    MEGA error: {build.megaError}
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
